import { Component, OnInit, viewChild, DestroyRef, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Integration } from '../../../models/integration';
import { ClientInformation } from 'src/app/models/client-information';
import { IntegrationError } from '../../../models/integration-error';
import { ClientIntegrationService } from '../../../services/client-integration.service';
import { IntegrationErrorsService } from '../../../services/integration-errors.service';
import { ToastService } from '../../../services/toast.service';
import { Server } from 'src/app/models/server';
import { TypeConstantService } from 'src/app/services/type-constant.service';
import { ErrorCategory } from 'src/app/models/error-category';
import { CurrentStatus } from 'src/app/models/currentStatus';
import { ReportErrorSearchFilter } from 'src/app/models/report-error-search-filter';
import { ReportService } from 'src/app/services/report.service';
import { ReportErrorSearchFilterComponent } from 'src/app/features/lt-errors/components/report-error-search-filter/report-error-search-filter.component';
import { ReportConstants } from 'src/app/models/report-constants';
import { passUntilDestroyed } from 'src/app/utilities/rx-operators';
import { LTErrorsGridComponent } from 'src/app/features/lt-errors/components/lt-errors-grid/lt-errors-grid';

@Component({
  selector: 'app-integration-error-report-view-v3',
  templateUrl: './integration-error-report-view-v3.html',
  styleUrls: ['./integration-error-report-view-v3.scss'],
  providers: [ClientIntegrationService, IntegrationErrorsService, ReportService],
  standalone: false,
})
export class IntegrationErrorReportViewV3Component implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  reportErrorsGridComponent = viewChild<LTErrorsGridComponent>('reportErrorsGridComponent');
  reportErrorsFilter = viewChild<ReportErrorSearchFilterComponent>('reportErrorsFilter');

  tabIndex = 0;
  dataLoading = true;
  errorsPopulated = false;

  fullErrorsList: IntegrationError[] = [];
  filteredErrorList: IntegrationError[] = [];
  selectedErrorIds: Map<string, Date>;
  filteredErrorIds: Map<string, Date>;
  errorGroups: Map<string, Date>[];

  public categories: ErrorCategory[];
  public statuses: CurrentStatus[];
  reportConstants: ReportConstants;

  integrations: Integration[];
  clients: ClientInformation[];
  servers: Server[];
  integrationsMap: Map<string, Integration>;
  totalErrors = 0;
  errorGroupSize = 500;
  defaultErrorWindow = 90;

  errorSearchFilter: ReportErrorSearchFilter;
  defaultErrorSearchFilter: ReportErrorSearchFilter;

  hasCategories = false;
  hasStatuses = false;
  gettingClients = false;
  firstGroupReturned = false;
  allGroupsReturned = false;
  gridFullyPopulated = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private clientService: ClientIntegrationService,
    private reportService: ReportService,
    private toastService: ToastService,
    private errorLoggingService: IntegrationErrorsService,
    private typeConstantService: TypeConstantService) {

  }

  ngOnInit(): void {
    this.defaultErrorSearchFilter = {
      numberOfDays: this.defaultErrorWindow,
      startReceivedDate: new Date(),
      dataSproc: null,
    };

    this.defaultErrorSearchFilter.startReceivedDate.setDate((new Date().getDate() - this.defaultErrorWindow));

    this.errorSearchFilter = Object.assign({}, this.defaultErrorSearchFilter);
    this.resetAll();
    this.getTypeConstants();
    this.getReportConstants();

    this.getClients();
    this.getIntegrations();
    this.getAllErrors();
  }

  getAllErrors(): void {
    this.dataLoading = true;
    this.errorsPopulated = false;

    this.reportService.getIntegrationErrorIdsForReport(this.errorSearchFilter)
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe(
        (data) => {
          this.selectedErrorIds = data;
          this.getErrorsByIdList();
        },
        (_error) => {
          this.dataLoading = false;
          this.errorsPopulated = true;
          this.toastService.toastCreate('Loading Integration Errors Failed: ' + _error, 'Warning', { autoClose: true, keepAfterRouteChange: false });
        },
      );
  }

  getFilteredErrorIdListAndUpdateGrid(): void {
    this.reportService.getIntegrationErrorIdsForReport(this.errorSearchFilter)
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe(
        (data) => {
          this.filteredErrorIds = data;
          const originalIds = [];
          const newIds = [];
          this.selectedErrorIds.forEach(e => originalIds.push(e['key']));
          this.filteredErrorIds.forEach(e => newIds.push(e['key']));

          const missingErrors = newIds.filter(i => !originalIds.includes(i));
          if (missingErrors?.length > 0) {
            console.log('Found ' + missingErrors.length + ' Missing Errors from Full List with current filter, retrieving now...');
            this.getMissingErrorsByIdList(missingErrors);
          }
          else {
            this.updateReportFromFilter();
          }
        },
        (_error) => {
          this.dataLoading = false;
          this.errorsPopulated = true;
          this.toastService.toastCreate('Loading Integration Errors Failed: ' + _error, 'Warning', { autoClose: true, keepAfterRouteChange: false });
        },
      );
  }

  getMissingErrorsByIdList(errorGroup: string[]): void {
    this.errorLoggingService.getErrorsByErrorIdList(errorGroup)
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe(
        (data) => {
          this.fullErrorsList = this.fullErrorsList.concat(data);
          console.log('Returned ' + data?.length + ' Missing Errors to add to Full List with current filter');
          this.reportErrorsGridComponent()?.refresh();
          this.updateReportFromFilter();
          this.allGroupsReturned = true;
          this.reportErrorsFilter()?.setAllErrorsReturned();
        },
        (_error) => {
          this.dataLoading = false;
          this.errorsPopulated = true;
          this.toastService.toastCreate('Loading First Integration Error Group Failed: ' + _error, 'Warning', { autoClose: true, keepAfterRouteChange: false });
        },
      );
  }

  updateReportFromFilter() {
    this.reportErrorsGridComponent().filterByReportFilters(this.errorSearchFilter);
    this.reportErrorsGridComponent()?.refresh();

    this.errorsPopulated = true;
    this.firstGroupReturned = true;
    this.dataLoading = false;
  }

  getErrorsByIdList(): void {
    this.errorGroups = this.createGroups(Array.from(this.selectedErrorIds), this.errorGroupSize);
    const firstErrorGroup = [...this.errorGroups[0].keys()];
    this.getFirstErrorGroup(firstErrorGroup);
  }

  createGroups(arr: [string, Date][], perGroup: number): Map<string, Date>[] {
    const rc: Map<string, Date>[] = [];
    const numGroups = Math.ceil(arr.length / perGroup);
    const tmpArr = new Array(numGroups)
      .fill('')
      .map((_, i) => arr.slice(i * perGroup, (i + 1) * perGroup));

    tmpArr.forEach(i => rc.push(new Map(i.map(x => [x['key'], x['value']] as [string, Date]))));
    return rc;
  }

  // sendUpdatedDataToTable(e: any) {
  //   this.getErrorsByIdList();
  // }

  getFirstErrorGroup(errorGroup: string[]): void {
    this.errorLoggingService.getErrorsByErrorIdList(errorGroup)
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe(
        (data) => {
          this.fullErrorsList = this.fullErrorsList.concat(data);
          this.errorsPopulated = true;
          this.firstGroupReturned = true;
          this.dataLoading = false;
          this.errorsPopulated = true;
          this.reportErrorsGridComponent()?.refresh();

          this.getOtherErrorGroupsInBackground();
        },
        (_error) => {
          this.dataLoading = false;
          this.errorsPopulated = true;
          this.toastService.toastCreate('Loading First Integration Error Group Failed: ' + _error, 'Warning', { autoClose: true, keepAfterRouteChange: false });
        },
      );
  }

  getOtherErrorGroupsInBackground(): void {
    for (let i = 1; i < this.errorGroups.length; i++) {
      this.errorLoggingService.getErrorsByErrorIdList(Array.from([...this.errorGroups[i].keys()]))
        .pipe(passUntilDestroyed(this.destroyRef))
        .subscribe(
          (data) => {
            this.fullErrorsList = this.fullErrorsList.concat(data);
            if (i == (this.errorGroups.length - 1)) {
              this.allGroupsReturned = true;
              this.reportErrorsFilter()?.setAllErrorsReturned();
            }
          },
          (_error) => {
            this.dataLoading = false;
            this.errorsPopulated = true;
            this.toastService.toastCreate('Loading Integration Error Group ' + i + ' Failed: ' + _error, 'Warning', { autoClose: true, keepAfterRouteChange: false });
          },
        );
    }
  }

  reloadGridForFullPopulation(_event: boolean): void {
    this.dataLoading = true;
    this.errorsPopulated = false;
    this.reportErrorsGridComponent().refresh();
    this.errorsPopulated = true;
    this.dataLoading = false;
    this.reportErrorsFilter().setAllErrorsDisplayed();
  }

  filterErrorsReport(event: ReportErrorSearchFilter): void {
    //If these Conditions are met (ie. Previous Filter was Default Filter), all Filtered errors should already be in the report
    if (this.errorSearchFilter.startReceivedDate <= event.startReceivedDate && this.errorSearchFilter.endReceivedDate >= event.endReceivedDate
      && this.errorSearchFilter.destinationEndpointId == undefined && this.errorSearchFilter.importTypeId == undefined
      && this.errorSearchFilter.errorCategoryId == undefined && this.errorSearchFilter.currentStatusId == undefined && this.errorSearchFilter.integrationId == undefined) {
      this.reportErrorsGridComponent().filterByReportFilters(event);
      this.errorSearchFilter = event;
    }
    else {
      this.errorSearchFilter = event;
      this.reportErrorsFilter().allErrorsDisplayed = false;
      this.reportErrorsFilter().allErrorsReturned = false;
      this.dataLoading = true;
      this.getFilteredErrorIdListAndUpdateGrid();
    }
  }

  public getReportConstants(): void {
    this.typeConstantService.getAllReportViewConstants()
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe(
        (data) => {
          if (data != null) {
            this.reportConstants = data;
          }
        },
        (_error) => {
          // console.log(error);
          this.toastService.toastCreate('Report Constants failed to return', 'Danger');
        },
      );
  }

  getTypeConstants(): void {
    this.errorLoggingService.getAllErrorCategories()
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe(r => {
        this.categories = r;
        this.hasCategories = true;
      },
        (_error) => {
          this.hasCategories = false;
        });

    this.errorLoggingService.getAllCurrentStatuses()
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe(r => {
        this.statuses = r;
        this.hasStatuses = true;
      },
        (_error) => {
          this.hasStatuses = false;
        });
  }

  getIntegrations(): void {
    this.clientService.getAllIntegrations()
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (g) => {
          this.integrationsMap = new Map(g.map(k => [k.integrationId, k] as [string, Integration]));
          this.integrations = g;
        },
        error: (_error) => {
          this.gettingClients = false;
          this.toastService.toastCreate('Error getting clients: ' + _error, 'Warning', { autoClose: true, keepAfterRouteChange: false });
        },
      });
  }

  clearFilters(_event: boolean): void {
    this.reportErrorsGridComponent().clearFilters();
  }

  getClients(): void {
    this.gettingClients = true;
    this.clientService.getAllClients()
      .pipe(passUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (clients) => {
          this.clients = clients;
          this.gettingClients = false;
        },
        error: (_error) => {
          this.gettingClients = false;
          this.toastService.toastCreate('Error getting clients: ' + _error, 'Warning', { autoClose: true, keepAfterRouteChange: false });
        },
      });
  }

  resetAll(): void {
    this.tabIndex = 0;
    this.dataLoading = true;
    this.errorsPopulated = false;
    this.fullErrorsList = [];
    this.integrations = [];
  }

  public handleUpdatedIntegrationErrorsFromGrid(updatedErrors: IntegrationError[]): void {
    if (updatedErrors === null || updatedErrors.length === 0) {
      return;
    }

    updatedErrors.forEach(updatedError => {
      const allIndex = this.fullErrorsList.findIndex(x => x.integrationErrorId === updatedErrors[0].integrationErrorId);
      if (allIndex !== -1) {
        this.fullErrorsList[allIndex] = updatedError;
      }
    });
  }
}
